import Axios, { AxiosRequestConfig } from 'axios';
import credentialsService from 'services/credentialsService';
import env from 'env';
import bus, { AuthEventTypes } from 'modules/bus';
import refreshTokenService from 'services/refreshTokenService';

const httpClient = Axios.create({
  baseURL: `${env.SERVER_ENDPOINT}/`,
});

const setToken = (config: AxiosRequestConfig) => {
  const { token } = credentialsService;

  if (token && config.method !== 'OPTIONS') {
    config.headers.Authorization = `Bearer ${token}`;
  }
};

const refresh = async (config: AxiosRequestConfig) => {
  await new Promise<void>((resolve, reject) => {
    function onRefreshTokenSuccess() {
      setToken(config);
      removeHandlers();
      resolve();
    }

    function onRefreshTokenFail() {
      reject(Error('Could not refresh token'));
      removeHandlers();
    }

    const removeSuccessListener = bus.addEventListener(
      AuthEventTypes.REFRESH_TOKEN_SUCCESS,
      onRefreshTokenSuccess,
    );
    const removeFailListener = bus.addEventListener(
      AuthEventTypes.REFRESH_TOKEN_FAIL,
      onRefreshTokenFail,
    );

    function removeHandlers() {
      removeSuccessListener();
      removeFailListener();
    }

    setTimeout(() => {
      reject(Error('Could not refresh token'));
      removeHandlers();
      refreshTokenService.refreshingAtm = false;
    }, refreshTokenService.checkRefreshingTreshold);
  });
};

httpClient.interceptors.request.use(
  async function (config) {
    // Do something before request is sent
    // If the header does not contain the token and the url not public, redirect to login

    setToken(config);
    if (refreshTokenService.refreshingAtm) {
      await refresh(config);
    }

    return config;
  },
  function (error) {
    return Promise.reject(error);
  },
);

httpClient.interceptors.response.use(null, async (error) => {
  const { response, config } = error;

  if (response?.status === 403) {
    bus.broadcastEvent(AuthEventTypes.FORBIDDEN_SIGNAL);
    return Promise.reject(error);
  }

  if (response?.status === 401) {
    if (refreshTokenService.triedRefresh) {
      bus.broadcastEvent(AuthEventTypes.LOGOUT_SIGNAL);
      return Promise.reject(error);
    }

    if (refreshTokenService.refreshingAtm) {
      await refresh(config);
      return httpClient(config);
    }

    try {
      await refreshTokenService.refreshToken();
      return httpClient(config);
    } catch (err) {
      bus.broadcastEvent(AuthEventTypes.LOGOUT_SIGNAL);
      error.preventDefault = true;
    }
  }
  return Promise.reject(error);
});

export default httpClient;
